import Swal from "sweetalert2";
import moment from "moment-timezone";
import { CurrenciesAPI } from "../../../core/api";
/**
 * Validates the direction inputs based on the transfer type.
 *
 * @param {string} transferType - The type of the transfer (e.g., 'transfer', 'hourly', etc.).
 * @param {string} from - The starting location of the transfer.
 * @param {string} to - The destination location of the transfer.
 * @returns {boolean} - Returns true if the direction inputs are valid, false otherwise.
 */
export function isValidDirection(transferType, from, to) {
    if (transferType !== "hourly") {
        return to !== "" && from !== ""
    }
    return from !== "";
}

/**
 * Validates the date and time inputs for a reservation.
 *
 * @param {string} pickupDate - The date of pickup in 'YYYY-MM-DD' format.
 * @param {string} pickupTime - The time of pickup in 'HH:MM' format.
 * @returns {boolean} - Returns true if both date and time are provided, false otherwise.
 */
export function isValidDateAndTime(pickupDate, pickupTime) {
    return pickupDate !== "" && pickupTime !== "";
}

/**
 * Validates the passenger count for a reservation.
 *
 * @param {number} passengersCount - The number of passengers for the reservation.
 * @returns {boolean} - Returns true if the passenger count is valid, false otherwise.
 */
export function isValidPassengerCount(passengersCount) {
    return Number.isInteger(passengersCount) && passengersCount > 0;
}

/**
 * Validates the number of hours for an hourly transfer.
 *
 * @param {string} transferType - The type of the transfer (e.g., 'transfer', 'hourly', etc.).
 * @param {number} hours - The number of hours for the reservation.
 * @returns {boolean} - Returns true if the number of hours is valid for an hourly transfer, false otherwise.
 */
export function isValidNumerHours(transferType, quotedHours) {
    if (transferType === "hourly") {
        return Number.isInteger(quotedHours) && quotedHours > 0;
    }
    return true;
}

/**
 * Determines if a reservation can be requoted based on various parameters.
 *
 * @param {string} transferType - The type of the transfer (e.g., 'transfer', 'hourly', etc.).
 * @param {string} from - The starting location of the reservation.
 * @param {string} to - The destination location of the reservation.
 * @param {string} pickupDate - The date of pickup in 'YYYY-MM-DD' format.
 * @param {string} pickupTime - The time of pickup in 'HH:MM' format.
 * @param {number} passengersCount - The number of passengers for the reservation.
 * @returns {boolean} - Returns true if the reservation can be requoted, false otherwise.
 */
export function canBeRequoted(transferType, from, to, pickupDate, pickupTime, quotedHours, passengersCount) {

    if (!isValidDirection(transferType, from, to)) {
        return false;
    }

    if (!isValidDateAndTime(pickupDate, pickupTime)) {
        return false;
    }

    if (!isValidPassengerCount(passengersCount)) {
        return false;
    }

    if (!isValidNumerHours(transferType, quotedHours)) {
        return false;
    }

    return true;
}

/**
 * Formats a date to the "DD-MMM-YYYY" format for sold-out notifications.
 *
 * @param {string|Date} date - The date to format.
 * @returns {string} - The formatted date string.
 */
export function formatBlackoutDate(date) {
    return moment.parseZone(date).format("DD-MMM-YYYY");
}

/**
 * Gets the unit format string based on the provided unit code.
 *
 * @param {string} unit - The unit code (e.g., 'HOURS', 'DAYS', etc.).
 * @returns {string} - The corresponding unit format string.
 */
export function getUnitFormat(unit) {
    if (unit === "HOURS") {
        return "hours";
    } else if (unit === "DAYS") {
        return "days";
    } else {
        return "months";
    }
}

/**
 * Builds a UTC datetime string from the provided pickup date and time.
 *
 * @param {string} pickupDate - The date of pickup in 'YYYY-MM-DD' format.
 * @param {string} pickupTime - The time of pickup in 'HH:MM AM/PM' format.
 * @returns {string} - The combined UTC datetime string in ISO format.
 */
export function buildUtcPickupDateTime(pickupDate, pickupTime) {
    const parsedDate = moment(pickupDate);
    const parsedTime = moment(pickupTime, "h:mm A");
    const combined = parsedDate.set({
        hour: parsedTime.hour(),
        minute: parsedTime.minute(),
        second: parsedTime.second()
    });
    return combined.utc(true).format();
}

/**
 * Handles the confirmation of a blackout period for a service.
 *
 * @param {Object} service - The service object containing blackout information.
 * @returns {Promise<void>}
 */
export async function handleBlackoutConfirmation(service, callback = async function () { }) {
    const { start_date, end_date } = service.blackout;
    let { value: confirmed } = await Swal.fire({
        title: "BlackOut Date",
        html: `The selected service has a <br> blackout period in effect from <br><strong>${formatBlackoutDate(start_date)} </strong> to <strong>${formatBlackoutDate(end_date)}</strong>. </br></br> Are you sure do you want continue?`,
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
        buttonsStyling: false,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        allowOutsideClick: false,
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-primary btn-small-one"
    });
    callback(confirmed, service);
}

/** * Handles the confirmation of an advanced booking policy for a service.
 *
 * @param {Object} service - The service object containing advanced booking policy information.
 * @returns {Promise<void>}
 */
export async function handleCutOffConfirmation(service, callback = async function () { }) {
    const { value, unit } = service.cutoffDuration;
    let { value: confirmed } = await Swal.fire({
        title: "Advanced booking policy!",
        html: `This Supplier has an advanced booking policy of </br><strong>${value} ${getUnitFormat(unit)}.</strong></br></br> Do you still want to update this reservation?`,
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
        buttonsStyling: false,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-primary btn-small-one"
    });
    callback(confirmed, service);
}

/**
 * Returns true if the reservation status blocks re-quoting.
 * Cancelled ("CANCELLED"), completed ("JF"), and no-show ("NS") reservations
 * must not trigger a new quote when a requotable field is modified.
 *
 * @param {string} reservationStatus - The current status code of the reservation.
 * @returns {boolean}
 */
export function isRequoteBlockedByStatus(reservationStatus) {
    return ["SCB", "SBC", "JF", "NS"].includes(reservationStatus);
}

/**
 * Checks if the service has available seats for the given number of passengers.
 *
 * @param {Object} service - The service object containing vehicle information.
 * @param {number} passengersCount - The number of passengers for the reservation.
 * @returns {boolean} - Returns true if there are available seats, false otherwise.
 */
export function hasAvailableSeats(service, passengersCount) {
    if (!service) return false;

    const { vehicle } = service;

    if (!vehicle) return false;

    return passengersCount <= vehicle.seats;
}

/** * Determines the quoting trigger for a service based on its properties.
 *
 * @param {Object} service - The service object to evaluate.
 * @returns {string|null} - Returns the quoting trigger ("CUT_OFF_TIME", "AVAILABILITY_ON_REQUEST") or null if none applies.
 */
export function resolveConfirmationReason(stage, service) {

    if (stage && stage === "QUOTE") return null;

    if (!service) return null;

    if (service.isCutoffTimeApplied) {
        return "CUT_OFF_TIME";
    }

    if (service.availabilityOnRequest) {
        return "AVAILABILITY_ON_REQUEST";
    }

    return null;
}

/**
 * Determines whether a location field should be typed as an airport or a point of interest.
 *
 * @param {string} type - The transfer type ("arrival", "departure", etc.).
 * @param {string} origin - Which field is being resolved ("from" or "to").
 * @returns {string} - "airport" or "point_of_interest".
 */
export function getTypeLocation(type, origin) {
    if (type === "arrival" && origin === "from") {
        return "airport";
    } else if (type === "departure" && origin === "to") {
        return "airport";
    } else {
        return "point_of_interest";
    }
}

/**
 * Resolves which origin data (rich location object vs plain string) to send.
 * Airports are sent as objects on arrivals; non-airport Google selections are sent as objects
 * so the backend can geocode them by place_id. Otherwise the plain "from" value is sent.
 *
 * @param {Object|null} originData - The rich origin location object, if any.
 * @param {string} from - The plain "from" value.
 * @param {string} reservationType - The reservation's transfer type.
 * @returns {Object|string} - The origin data to send.
 */
export function getOriginData(originData, from, reservationType) {
    if (reservationType === "arrival" && originData) {
        return originData.location ? originData : from;
    } else {
        return isSelectedGooglePlace(originData, from) ? originData : from;
    }
}

/**
 * Resolves which destination data (rich location object vs plain string) to send.
 * Airports are sent as objects on departures; non-airport Google selections are sent as objects
 * so the backend can geocode them by place_id. Otherwise the plain "to" value is sent.
 *
 * @param {Object|null} destinationData - The rich destination location object, if any.
 * @param {string} to - The plain "to" value.
 * @param {string} reservationType - The reservation's transfer type.
 * @returns {Object|string} - The destination data to send.
 */
export function getDestinationData(destinationData, to, reservationType) {
    if (reservationType === "departure" && destinationData) {
        return destinationData.location ? destinationData : to;
    } else {
        return isSelectedGooglePlace(destinationData, to) ? destinationData : to;
    }
}

/**
 * Checks if a location is a non-airport Google selection that still matches the current address.
 *
 * @param {Object|null} location - The selected location data.
 * @param {string} address - The current address string.
 * @returns {boolean}
 */
function isSelectedGooglePlace(location, address) {
    return !!location &&
        location.type !== "airport" &&
        !!location.place_id &&
        location.select === address;
}

/**
 * Finds a service object in a list by its id.
 *
 * @param {string} serviceId - The id of the service to find.
 * @param {Array} services - The list of services to search.
 * @returns {Object|null} - The matching service, or null if not found.
 */
export function getServiceById(serviceId, services = []) {
    return services.find(service => service._id === serviceId) || null;
}

/**
 * Converts a gross price from one currency to another using the current exchange rate.
 *
 * @param {number} grossPrice - The amount to convert.
 * @param {string} fromCurrencyCode - The source currency code.
 * @param {string} toCurrencyCode - The target currency code.
 * @returns {Promise<number>} - The converted amount, or 0 if the conversion failed.
 */
export async function conversionCurrency(grossPrice, fromCurrencyCode, toCurrencyCode) {
    try {
        let { data } = await CurrenciesAPI.conversionCurrency(
            fromCurrencyCode,
            toCurrencyCode
        );
        if (data) {
            if (data.conversion_rate) {
                let rate = parseFloat(data.conversion_rate.toFixed(5));
                return rate * grossPrice;
            } else {
                return 0;
            }
        } else {
            return 0;
        }
    } catch (error) {
        return 0;
    }
}

/**
 * Returns the gross price converted to the target currency if it differs from the source currency.
 *
 * @param {number} grossPrice - The amount to convert.
 * @param {string} fromCurrencyCode - The source currency code.
 * @param {string} toCurrencyCode - The target currency code.
 * @returns {Promise<number>} - The gross price in the target currency.
 */
export async function calculateGroosPrice(grossPrice, fromCurrencyCode, toCurrencyCode) {
    if (fromCurrencyCode === toCurrencyCode) {
        return grossPrice;
    } else {
        return await conversionCurrency(
            grossPrice,
            fromCurrencyCode,
            toCurrencyCode
        );
    }
}

/**
 * Resolves the reservation's transfer type against the allowed types list.
 *
 * @param {Array<string>} types - The allowed reservation types.
 * @param {string} type - The candidate type (e.g. "airport").
 * @param {boolean} movement - Whether the movement is an arrival.
 * @returns {string} - The resolved reservation type.
 */
export function getReservationType(types, type, movement) {
    if (types.indexOf(type) > -1) {
        return type;
    } else {
        return type === "airport" && movement ? "arrival" : "departure";
    }
}

/**
 * Returns the cached reference value only if it matches the current transfer type.
 *
 * @param {string} transferType - The current transfer type.
 * @param {Object} routerReference - The cached { transferType, value } reference.
 * @returns {string|number} - The cached value, or a default for the current type.
 */
export function getRouterReferenceValue(transferType, routerReference) {
    if (transferType === routerReference.transferType) {
        return routerReference.value;
    } else {
        return transferType === "hourly" ? 0 : "";
    }
}

/**
 * Builds a { transferType, value } object picking the right underlying field based on transfer type.
 *
 * @param {string} transferType - The current transfer type.
 * @param {string} flightNumber - The flight number field value.
 * @param {string} stationNumber - The station number field value.
 * @param {string} cruiseLine - The cruise line field value.
 * @param {number} numberHourly - The hourly duration field value.
 * @returns {Object} - The resolved { transferType, value } reference.
 */
export function getRouterReference(
    transferType,
    flightNumber,
    stationNumber,
    cruiseLine,
    numberHourly
) {
    if (["arrival", "departure"].includes(transferType)) {
        return {
            transferType: transferType,
            value: flightNumber
        };
    } else if (transferType === "station") {
        return {
            transferType: transferType,
            value: stationNumber
        };
    } else if (transferType === "port") {
        return {
            transferType: transferType,
            value: cruiseLine
        };
    } else if (transferType === "hourly") {
        return {
            transferType: transferType,
            value: numberHourly
        };
    } else {
        return {
            transferType: transferType,
            value: ""
        };
    }
}

/**
 * Comparator used to check if a given service option matches the currently selected service.
 *
 * @param {Object|null} service - The currently selected service, if any.
 * @param {Object} element - The service option being compared.
 * @returns {boolean} - True if they match.
 */
export function getSelectServices(service, element) {
    if (service) {
        return service._id === element._id;
    }
    return false;
}

/**
 * Returns whether price/discount/extras adjustments are allowed (service and customer both selected).
 *
 * @param {Object|null} service - The currently selected service.
 * @param {Object|null} customer - The currently selected customer.
 * @returns {boolean} - True if adjustments are allowed.
 */
export function getAdjustmentsStatus(service, customer) {
    return service && customer ? true : false;
}

/**
 * Resets the assigned driver when the service's branch changes.
 *
 * @param {Object} driver - The currently assigned driver.
 * @param {string} oldbranch - The branch the driver was assigned under.
 * @param {string} currentBranch - The newly selected branch.
 * @returns {Object|null} - The driver if the branch didn't change, otherwise null.
 */
export function driverAssign(driver, oldbranch, currentBranch) {
    if (currentBranch === oldbranch) {
        return driver;
    }
    return null;
}

/**
 * Computes the gross price from a net price using a markup or discount percentage.
 *
 * @param {number} netPrice - The net price to apply the markup/discount to.
 * @param {number} markUp - The markup/discount percentage.
 * @param {string} markUpType - "markup" to apply a markup, otherwise a discount.
 * @param {boolean} customerExists - Whether a customer is currently selected.
 * @returns {number} - The computed gross price.
 */
export function getGroosPrice(netPrice, markUp, markUpType, customerExists) {
    if (customerExists) {
        return markUpType === "markup"
            ? netPrice / (1 - markUp / 100)
            : netPrice * (1 - markUp / 100);
    }
    return netPrice / 0.7;
}

/**
 * Checks whether a latitude/longitude pair is within valid geographic bounds.
 *
 * @param {number} lat - The latitude to validate.
 * @param {number} lng - The longitude to validate.
 * @returns {boolean} - Returns true if both coordinates are valid numbers within range.
 */
export function isValidCoordinates(lat, lng) {
    const isLatValid = typeof lat === "number" && lat >= -90 && lat <= 90;
    const isLngValid = typeof lng === "number" && lng >= -180 && lng <= 180;
    return isLatValid && isLngValid;
}

/**
 * Resolves the location sent to the product quotation.
 * Uses the structured point (e.g. airport) when it applies to the transfer type and has valid coordinates,
 * otherwise falls back to the plain direction.
 *
 * @param {boolean} isTransferType - Whether the point applies to the product transfer type (ARRIVAL origin / DEPARTURE destination).
 * @param {Object|null} point - The structured location data.
 * @param {string} direction - The plain direction text.
 * @returns {Object|string|null} - The structured point, the direction, or null.
 */
export function getPoint(isTransferType, point, direction) {
    if (isTransferType && point) {
        return isValidCoordinates(point.location.lat, point.location.lng)
            ? {
                select: point.select,
                location: point.location,
                owner: point.owner,
                country_code: point.country_code,
                place_id: point.place_id,
                type: point.type
            }
            : direction;
    } else {
        return direction ? direction : null;
    }
}
